import React, { useState, useRef, useEffect } from 'react';
import { ArrowDownOutlined } from '@ant-design/icons';
import {
  OptionItem,
  OptionsList,
  SelectButton,
  SelectContainer,
  SelectedContent,
  SelectedLabel,
  ClearButton,
} from './styles';

interface Option {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  placeholderIcon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Selecione...',
  placeholderIcon,
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <SelectContainer ref={containerRef} disabled={disabled} className={className}>
      <SelectButton
        type="button"
        isOpen={isOpen}
        isPlaceholder={!value}
        onClick={() => setIsOpen(!isOpen)}
      >
        <SelectedContent>
          {placeholderIcon && !value && placeholderIcon}
          <SelectedLabel title={selectedOption?.label}>
            {selectedOption?.label || placeholder}
          </SelectedLabel>
        </SelectedContent>
        {value && !disabled && (
          <ClearButton onClick={handleClear} aria-label="Clear selection">
            ✕
          </ClearButton>
        )}
        {!value && <ArrowDownOutlined className="arrow" />}
      </SelectButton>

      <OptionsList isOpen={isOpen}>
        {options.map((option) => (
          <OptionItem
            key={option.value}
            isSelected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            <span>{option.label}</span>
          </OptionItem>
        ))}
      </OptionsList>
    </SelectContainer>
  );
};
